// src/pages/TrackOrderPage.js
import React, { useState, useEffect } from 'react';
import { ordersApi, getErrorMessage } from '../services/api';

// ── Status config ─────────────────────────────────────────────────────────────
const STATUSES = [
  {
    key:     'pending',
    label:   'Order Placed',
    icon:    '🧾',
    color:   'bg-amber-100 text-amber-700 border-amber-300',
    dot:     'bg-amber-400',
    desc:    'We have received your order and are verifying the details.',
  },
  {
    key:     'confirmed',
    label:   'Order Confirmed',
    icon:    '✅',
    color:   'bg-blue-100 text-blue-700 border-blue-300',
    dot:     'bg-blue-500',
    desc:    'Your order has been confirmed! We are preparing it for packing.',
  },
  {
    key:     'processing',
    label:   'Packing in Progress',
    icon:    '📦',
    color:   'bg-purple-100 text-purple-700 border-purple-300',
    dot:     'bg-purple-500',
    desc:    'Your crackers are being carefully packed with safety measures at our Sivakasi warehouse.',
  },
  {
    key:     'shipped',
    label:   'Out for Delivery',
    icon:    '🚚',
    color:   'bg-orange-100 text-orange-700 border-orange-300',
    dot:     'bg-orange-500',
    desc:    'Your order is on its way! Our transport partner has picked up your package from Sivakasi.',
  },
  {
    key:     'delivered',
    label:   'Delivered',
    icon:    '🎆',
    color:   'bg-green-100 text-green-700 border-green-300',
    dot:     'bg-green-500',
    desc:    'Your order has been delivered successfully. Enjoy the celebrations! Happy Diwali! 🪔',
  },
  {
    key:     'cancelled',
    label:   'Cancelled',
    icon:    '❌',
    color:   'bg-red-100 text-red-700 border-red-300',
    dot:     'bg-red-500',
    desc:    'This order has been cancelled. Please contact us on WhatsApp if you have any questions.',
  },
];

const getStatusInfo = (status) =>
  STATUSES.find(s => s.key === status) || STATUSES[0];

// ── Progress stepper (only for non-cancelled) ─────────────────────────────────
const PROGRESS_STEPS = STATUSES.filter(s => s.key !== 'cancelled');

function OrderProgress({ status }) {
  const currentIndex = PROGRESS_STEPS.findIndex(s => s.key === status);
  if (status === 'cancelled') return null;

  return (
    <div className="relative">
      {/* connecting line */}
      <div className="absolute top-7 left-0 right-0 h-1 bg-gray-200 mx-10 rounded-full"/>
      <div
        className="absolute top-7 left-0 h-1 bg-gradient-to-r from-red-600 to-amber-400 mx-10 rounded-full transition-all duration-700"
        style={{ width: `${currentIndex / (PROGRESS_STEPS.length - 1) * 100}%`,
                 right: 'auto', maxWidth: 'calc(100% - 5rem)' }}
      />

      <div className="relative flex justify-between">
        {PROGRESS_STEPS.map((step, idx) => {
          const done    = idx <= currentIndex;
          const current = idx === currentIndex;
          return (
            <div key={step.key} className="flex flex-col items-center gap-2 flex-1">
              {/* circle */}
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl
                border-4 transition-all duration-500 z-10
                ${done
                  ? 'bg-gradient-to-br from-red-600 to-amber-500 border-amber-300 shadow-lg shadow-amber-200'
                  : 'bg-white border-gray-200'}`}>
                {step.icon}
              </div>
              {/* label */}
              <p className={`text-center text-[11px] font-bold leading-tight px-1
                ${done ? 'text-red-700' : 'text-gray-400'}`}>
                {step.label}
              </p>
              {/* current pulse */}
              {current && (
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400 animate-ping"/>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function TrackOrderPage() {
  const [orderId, setOrderId] = useState('');
  const [phone,   setPhone]   = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const handleTrack = async () => {
    setError(''); setResult(null);
    if (!orderId.trim()) { setError('Please enter your Order ID.'); return; }
    if (!phone.trim() || phone.replace(/\D/g,'').length < 10) {
      setError('Please enter a valid 10-digit mobile number.'); return;
    }
    setLoading(true);
    try {
      const res  = await ordersApi.trackOrder({ order_id: orderId.trim(), phone: phone.trim() });
      const data = res?.data || res;
      setResult(data);
    } catch (err) {
      const msg = getErrorMessage(err) || '';
      // Show friendly message for any mismatch or not found
      if (
        msg.toLowerCase().includes('mobile') ||
        msg.toLowerCase().includes('phone') ||
        msg.toLowerCase().includes('match') ||
        msg.toLowerCase().includes('not found') ||
        msg.toLowerCase().includes('invalid')
      ) {
        setError('Please enter the correct Order ID and mobile number. Both must match your original order.');
      } else {
        setError(msg || 'Please enter the correct Order ID and mobile number. Both must match your original order.');
      }
    } finally {
      setLoading(false);
    }
  };

  const statusInfo   = result ? getStatusInfo(result.status) : null;

  useEffect(() => {
    document.title = 'Track Your Order — Lakshmi Crackers';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-red-50 to-orange-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">

          {/* ── Header ── */}
          <div className="text-center mb-10">
            <div className="text-6xl mb-4">🚚</div>
            <h1 className="text-3xl font-black text-gray-900 mb-2">Track Your Order</h1>
            <p className="text-gray-500 text-sm">
              Enter your Order ID and registered mobile number to track your order status.
            </p>
          </div>

          {/* ── Search Form ── */}
          <div className="bg-white rounded-3xl shadow-xl border border-amber-100 p-8 mb-6">
            <div className="space-y-4">
              {/* Order ID */}
              <div>
                <label className="block text-sm font-black text-gray-700 mb-2">
                  Order ID <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">#</span>
                  <input
                    type="number"
                    value={orderId}
                    onChange={e => { setOrderId(e.target.value); setError(''); setResult(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleTrack()}
                    placeholder="e.g. 1001"
                    className="w-full pl-8 pr-4 py-3.5 border-2 border-gray-200 rounded-2xl
                      focus:outline-none focus:border-red-400 text-gray-900 font-bold
                      text-lg transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1 ml-1">
                  Your Order ID was shared with you on WhatsApp after placing the order.
                </p>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-black text-gray-700 mb-2">
                  Registered Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm">+91</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => { setPhone(e.target.value); setError(''); setResult(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleTrack()}
                    placeholder="e.g. 9876500000"
                    maxLength={10}
                    className="w-full pl-14 pr-4 py-3.5 border-2 border-gray-200 rounded-2xl
                      focus:outline-none focus:border-red-400 text-gray-900 font-bold
                      text-lg transition-colors"
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200
                  rounded-2xl px-4 py-3 animate-fade-in">
                  <span className="text-red-500 text-lg mt-0.5">⚠️</span>
                  <p className="text-red-600 text-sm font-semibold">{error}</p>
                </div>
              )}

              {/* Button */}
              <button
                onClick={handleTrack}
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800
                  hover:to-red-700 text-white font-black py-4 rounded-2xl text-lg
                  transition-all duration-300 shadow-lg shadow-red-200
                  disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3">
                {loading ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Tracking your order...
                  </>
                ) : (
                  <> 🔍 Track Order </>
                )}
              </button>
            </div>
          </div>

          {/* ── Result ── */}
          {result && statusInfo && (
            <div className="bg-white rounded-3xl shadow-xl border border-amber-100 overflow-hidden
              animate-fade-in">

              {/* Status header */}
              <div className={`px-8 py-6 border-b-2 ${statusInfo.color}`}>
                <div className="flex items-center gap-4">
                  <span className="text-5xl">{statusInfo.icon}</span>
                  <div className="flex-1">
                    <p className="text-xs font-black uppercase tracking-widest opacity-60 mb-1">
                      Current Status
                    </p>
                    <h2 className="text-2xl font-black">{statusInfo.label}</h2>
                    <p className="text-sm mt-1 opacity-80">{statusInfo.desc}</p>
                  </div>
                </div>
              </div>

              {/* Progress stepper */}
              {result.status !== 'cancelled' && (
                <div className="px-8 py-8 border-b border-gray-100">
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6">
                    Order Progress
                  </p>
                  <OrderProgress status={result.status}/>
                </div>
              )}

              {/* Order details */}
              <div className="px-8 py-6 space-y-4">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">
                  Order Details
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Order ID',     value: `#${result.order_id}`,    icon: '🧾' },
                    { label: 'Customer',     value: result.name,               icon: '👤' },
                    { label: 'Total Amount', value: `₹${parseInt(result.total).toLocaleString('en-IN')}`, icon: '💰' },
                    { label: 'Items',        value: `${result.items_count} item(s)`, icon: '📦' },
                    { label: 'Order Placed', value: result.created_at,         icon: '📅' },
                    { label: 'Last Updated', value: result.updated_at,         icon: '🔄' },
                  ].map(({label, value, icon}) => (
                    <div key={label} className="bg-gray-50 rounded-2xl px-4 py-3">
                      <p className="text-xs text-gray-400 font-semibold mb-1">{icon} {label}</p>
                      <p className="font-black text-gray-900 text-sm">{value}</p>
                    </div>
                  ))}
                </div>

                {/* Delivery address */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                  <p className="text-xs text-amber-600 font-semibold mb-1">📍 Delivery Address</p>
                  <p className="font-bold text-gray-800 text-sm">{result.address}</p>
                </div>
              </div>



              {/* Contact support */}
              <div className="px-8 pb-8">
                <div className="bg-gradient-to-r from-red-700 to-red-600 rounded-2xl px-5 py-4
                  flex items-center justify-between">
                  <div>
                    <p className="text-white font-black text-sm">Need Help?</p>
                    <p className="text-red-200 text-xs">Contact us on WhatsApp anytime</p>
                  </div>
                  <a
                    href={`https://wa.me/916374801083?text=Hi! I need help with my Order %23${result.order_id}`}
                    target="_blank" rel="noreferrer"
                    className="bg-white text-red-700 font-black text-sm px-5 py-2.5
                      rounded-xl hover:bg-red-50 transition-colors flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15
                        -.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463
                        -2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606
                        .134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371
                        -.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51
                        -.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016
                        -1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487
                        .709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758
                        -.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                      <path d="M11.99 2C6.476 2 2 6.477 2 11.99c0 1.79.468 3.47 1.28
                        4.934L2 22l5.235-1.253A9.956 9.956 0 0011.99 22C17.51 22 22
                        17.523 22 11.99 22 6.476 17.51 2 11.99 2zm0 18a7.98 7.98 0
                        01-4.075-1.116l-.292-.174-3.107.743.78-2.978-.19-.307A7.963
                        7.963 0 014 11.99C4 7.578 7.578 4 11.99 4 16.41 4 20 7.58
                        20 11.99 20 16.413 16.413 20 11.99 20z"/>
                    </svg>
                    WhatsApp Us
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ── Help box ── */}
          {!result && !loading && (
            <div className="bg-white rounded-3xl border border-amber-100 shadow-sm px-6 py-5">
              <p className="text-sm font-black text-gray-700 mb-3">💡 Where to find your Order ID?</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold mt-0.5">•</span>
                  Your Order ID was shared on <strong>WhatsApp</strong> after you placed the order
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold mt-0.5">•</span>
                  Check your <strong>email inbox</strong> for the order confirmation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold mt-0.5">•</span>
                  It is also visible in your <strong>account orders page</strong> if you are logged in
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold mt-0.5">•</span>
                  Can't find it? <a href="https://wa.me/916374801083" target="_blank" rel="noreferrer"
                    className="text-red-600 font-bold underline">WhatsApp us</a> and we'll help you instantly
                </li>
              </ul>
            </div>
          )}

        </div>
      </div>
  );
}
